/**
 * The phrase bank for Writing Expressions (ADR 0123 §3), English.
 *
 * Twelve shapes, each with every wording the sheet may print for it. The
 * placeholders are `{v}` (the variable letter), `{a}` (a multiplier or divisor
 * from 2 to 9) and `{b}` (a number from 2 to the dial). Each shape supplies
 * only the ones it names in `EXPRESSION_SHAPES`; a test fails if a phrasing
 * uses another.
 *
 * Every phrasing has one reading. The rules that keep it so are in the ADR:
 * no bare "plus", "minus" or "times" between two compound parts; a nested noun
 * phrase comes last; a comma closes a first operand only before "increased by"
 * or "decreased by"; "divided by" never follows a comparison; the variable is
 * mentioned once; no "half of", "thrice" or "squared".
 *
 * Phrases start lower case and have no full stop: they are fragments, as in
 * textbooks. A space before a placeholder is a no-break space (U+00A0, written
 * as an escape), so a letter or number never starts a line alone. `twice` marks
 * a phrasing that only reads correctly for a = 2.
 */

/** S1-S6 are one operation, T1-T6 two. See the table in ADR 0123 §2. */
export type ExpressionShape =
    | 'S1' | 'S2' | 'S3' | 'S4' | 'S5' | 'S6'
    | 'T1' | 'T2' | 'T3' | 'T4' | 'T5' | 'T6';

export interface ExpressionPhrasing {
    text: string;
    /** Only when the multiplier is 2: "twice", "el doble", "le double". */
    twice?: boolean;
}

/** The shapes, in the order ADR 0123 §2 lists them, with the placeholders each supplies. */
export const EXPRESSION_SHAPES: Record<ExpressionShape, readonly ('a' | 'b')[]> = {
    S1: ['b'],
    S2: ['b'],
    S3: ['b'],
    S4: ['a'],
    S5: ['a'],
    S6: ['b'],
    T1: ['a', 'b'],
    T2: ['a', 'b'],
    T3: ['a', 'b'],
    T4: ['a', 'b'],
    T5: ['a', 'b'],
    T6: ['a', 'b'],
};

export const expressionPhrases: Record<ExpressionShape, ExpressionPhrasing[]> = {
    S1: [
        { text: "the sum of a number\u00A0{v} and\u00A0{b}" },
        { text: "{b} more than a number\u00A0{v}" },
        { text: "a number\u00A0{v} increased by\u00A0{b}" },
    ],
    S2: [
        { text: "{b} less than a number\u00A0{v}" },
        { text: "a number\u00A0{v} decreased by\u00A0{b}" },
        { text: "the difference of a number\u00A0{v} and\u00A0{b}" },
    ],
    S3: [
        { text: "a number\u00A0{v} less than\u00A0{b}" },
        { text: "a number\u00A0{v} subtracted from\u00A0{b}" },
        { text: "the difference of\u00A0{b} and a number\u00A0{v}" },
    ],
    S4: [
        { text: "the product of\u00A0{a} and a number\u00A0{v}" },
        { text: "{a} times a number\u00A0{v}" },
        { text: "twice a number\u00A0{v}", twice: true },
    ],
    S5: [
        { text: "the quotient of a number\u00A0{v} and\u00A0{a}" },
        { text: "a number\u00A0{v} divided by\u00A0{a}" },
    ],
    S6: [
        { text: "the quotient of\u00A0{b} and a number\u00A0{v}" },
        { text: "{b} divided by a number\u00A0{v}" },
    ],
    T1: [
        { text: "{b} more than the product of\u00A0{a} and a number\u00A0{v}" },
        { text: "{b} more than\u00A0{a} times a number\u00A0{v}" },
        { text: "the product of\u00A0{a} and a number\u00A0{v}, increased by\u00A0{b}" },
        { text: "{b} more than twice a number\u00A0{v}", twice: true },
    ],
    T2: [
        { text: "{b} less than the product of\u00A0{a} and a number\u00A0{v}" },
        { text: "{b} less than\u00A0{a} times a number\u00A0{v}" },
        { text: "the product of\u00A0{a} and a number\u00A0{v}, decreased by\u00A0{b}" },
        { text: "{b} less than twice a number\u00A0{v}", twice: true },
    ],
    T3: [
        { text: "{b} decreased by the product of\u00A0{a} and a number\u00A0{v}" },
        { text: "the difference of\u00A0{b} and the product of\u00A0{a} and a number\u00A0{v}" },
        { text: "{b} decreased by twice a number\u00A0{v}", twice: true },
    ],
    T4: [
        { text: "{a} times the sum of a number\u00A0{v} and\u00A0{b}" },
        { text: "the product of\u00A0{a} and the sum of a number\u00A0{v} and\u00A0{b}" },
        { text: "twice the sum of a number\u00A0{v} and\u00A0{b}", twice: true },
    ],
    T5: [
        { text: "{a} times the difference of a number\u00A0{v} and\u00A0{b}" },
        { text: "the product of\u00A0{a} and the difference of a number\u00A0{v} and\u00A0{b}" },
        { text: "twice the difference of a number\u00A0{v} and\u00A0{b}", twice: true },
    ],
    T6: [
        { text: "{b} more than the quotient of a number\u00A0{v} and\u00A0{a}" },
        { text: "the quotient of a number\u00A0{v} and\u00A0{a}, increased by\u00A0{b}" },
    ],
};
